import { useState } from "react";
import { track } from "../../lib/analytics.ts";
import { type EntryLike, NOTE_KIND_TITLE } from "../../lib/entry-text.ts";
import { type NoteWord, splitOnWord } from "../../lib/note-words.ts";
import { clock, weekdayDay } from "../../lib/time.ts";
import { cn } from "../../lib/utils.ts";

const MIN_PX = 14;
const MAX_PX = 38;

/**
 * A word cloud of one baby's notes: the more notes mention a word, the bigger
 * and darker it's set. Words sit alphabetically so a word is easy to find
 * again. Tap one to read the notes that mention it, the word marked.
 */
export function NoteCloud({
  words,
  notes,
  loading = false,
  limited = false,
}: {
  words: NoteWord[];
  notes: EntryLike[];
  /** The two weeks are still on their way: don't call an empty cloud final. */
  loading?: boolean;
  /** The two weeks failed to load: only the live two days are here. */
  limited?: boolean;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const selected = words.find((w) => w.word === picked) ?? null;

  if (!words.length)
    return (
      <p className="py-3 text-[14px] italic opacity-60">
        {loading
          ? "Loading two weeks of notes…"
          : `No notes in the last ${limited ? "two days" : "two weeks"}. Words from notes show up here.`}
      </p>
    );

  const max = words[0]!.count;
  const min = Math.min(...words.map((w) => w.count));
  // Square root so a word in 9 notes is three times the step of one in 1, not nine.
  const weight = (count: number) => (max === min ? 0.5 : Math.sqrt((count - min) / (max - min)));
  const matching = selected
    ? notes
        .filter((n) => selected.noteIds.includes(n.id))
        .sort((a, b) => b.at.getTime() - a.at.getTime())
    : [];

  return (
    <div>
      <ul
        className="flex flex-wrap items-center justify-center gap-x-1 border-y border-rule py-3"
        aria-label="Words in notes"
      >
        {[...words]
          .sort((a, b) => a.word.localeCompare(b.word))
          .map((w) => {
            const k = weight(w.count);
            const on = w.word === picked;
            return (
              <li key={w.word}>
                <button
                  type="button"
                  onClick={() => {
                    // Whether a word was opened, never which word (it's from a note).
                    if (!on) track("note word tapped");
                    setPicked(on ? null : w.word);
                  }}
                  aria-pressed={on}
                  aria-label={`${w.word}, ${w.count} ${w.count === 1 ? "note" : "notes"}`}
                  title={`${w.count} ${w.count === 1 ? "note" : "notes"}`}
                  className={cn(
                    "min-h-[40px] rounded-md px-1.5 leading-none",
                    on
                      ? "bg-accent-tint text-accent-deep underline underline-offset-4"
                      : "hover:bg-rule",
                    k > 0.6 && "heading",
                  )}
                  style={{
                    fontSize: `${Math.round(MIN_PX + (MAX_PX - MIN_PX) * k)}px`,
                    // Never under 70%: the smallest words still clear 4.5:1.
                    opacity: on ? 1 : 0.7 + 0.3 * k,
                  }}
                >
                  {w.word}
                </button>
              </li>
            );
          })}
      </ul>
      {selected ? (
        <div className="mt-3" aria-live="polite">
          <p className="kicker">
            “{selected.word}” in {selected.count} {selected.count === 1 ? "note" : "notes"}
          </p>
          <ol className="mt-1.5">
            {matching.map((n) => (
              <li key={n.id} className="flex gap-3 border-t border-rule py-2 text-[14px]">
                <span className="w-[112px] shrink-0 whitespace-nowrap text-[12px] tabular-nums opacity-60">
                  {weekdayDay(n.at)} · {clock(n.at)}
                </span>
                <span className="min-w-0 break-words">
                  {n.noteKind && n.noteKind !== "general" ? (
                    <span className="heading">{NOTE_KIND_TITLE[n.noteKind]} — </span>
                  ) : null}
                  {splitOnWord(n.note ?? "", selected.word).map((part, i) =>
                    i % 2 ? (
                      <mark key={i} className="heading bg-transparent text-accent-deep">
                        {part}
                      </mark>
                    ) : (
                      part
                    ),
                  )}
                </span>
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <p className="mt-2 text-[12px] opacity-60">Tap a word to read the notes that mention it.</p>
      )}
    </div>
  );
}
